import axios from 'axios';

import { isDemoUser, isWriteOperation } from '../utils/demoProtection';

import type { InternalAxiosRequestConfig } from 'axios';

import { API_URL, MOCKS_ENABLED, NODE_ENV } from '@/env';
import i18n from '@/plugins/i18n';
import { addApiErrorBreadcrumb, addApiRequestBreadcrumb } from '@/plugins/Sentry';
import { showErrorToast } from '@/plugins/Toast';

const isTest = NODE_ENV === 'test';

const mockUrl = 'http://localhost';

const TOKEN_STORAGE_KEY = 'Token';

const api = axios.create({
    baseURL: isTest || MOCKS_ENABLED ? mockUrl : API_URL,
    withCredentials: true,
    paramsSerializer: {
        // Send arrays as param1=value1&param1=value2 instead of param1[0]=value1&param1[1]=value2
        indexes: null,
    },
});

/** React Query's own default; kept explicit so the retry predicate can reuse it. */
const DEFAULT_QUERY_RETRY_COUNT = 3;

const statusOf = (error: unknown) =>
    (error as { response?: { status?: number } })?.response?.status;

/**
 * A request the server refuses: 403 for a right the caller lacks, 404 for something missing or outside
 * their reach, which the server answers as if it did not exist. Neither is a fault, and this is the one
 * place that says so.
 */
export const isRefusedError = (error: unknown) => {
    const status = statusOf(error);
    return status === 403 || status === 404;
};

/**
 * How every query in the module behaves, declared here so the two federated entry points cannot drift:
 * each mounts its own client, and a reader reaching the same screen through the other one would
 * otherwise meet a different answer to the same outage.
 *
 * A refusal is deterministic: the same call without the permission, or outside the reader's reach, is
 * refused again. Retrying wastes requests and, because the retries are spread over seconds, it also
 * turns one refusal into several separate notifications.
 */
export const DEFAULT_QUERY_OPTIONS = {
    refetchOnWindowFocus: false,
    retry: (failureCount: number, error: unknown) =>
        !isRefusedError(error) && failureCount < DEFAULT_QUERY_RETRY_COUNT,
};

/** What the server said when it refused a request, whichever body shape it answered with. */
export interface RequestRefusal {
    status: number;
    /** The messages of each field it refused, keyed by the property path it names; empty when it names none. */
    fieldErrors: Record<string, string[]>;
    /** The refusal's stable code. */
    code?: string;
    /** The sentence, or the translation key, it refused with: `detail` of a problem document, `error` of the older body. */
    reason?: string;
    title?: string;
}

interface RefusalBody {
    errors?: unknown;
    code?: unknown;
    detail?: unknown;
    error?: unknown;
    title?: unknown;
}

const asText = (value: unknown) => (typeof value === 'string' ? value : undefined);

/**
 * The one reading of the standard fields of a refused request's body; a route whose refusal carries its own
 * payload still reads that payload itself. The server answers either with a problem document (RFC 9457:
 * `ValidationProblemDetails`, whose `errors` map the refused fields, or a document whose extensions such as
 * `code` serialize at its root) or with the older `{ error, code }` body. `null` when the request got no
 * answer or the answer is not a client error.
 */
export const readRefusal = (error: unknown): RequestRefusal | null => {
    const status = statusOf(error);

    if (status === undefined || status < 400 || status >= 500) {
        return null;
    }

    const body = ((error as { response?: { data?: unknown } }).response?.data ?? {}) as RefusalBody;
    const fieldErrors =
        body.errors && typeof body.errors === 'object'
            ? (body.errors as Record<string, string[]>)
            : {};

    return {
        status,
        fieldErrors,
        code: asText(body.code),
        reason: asText(body.detail) ?? asText(body.error),
        title: asText(body.title),
    };
};

/** Methods that fetch rather than change something. */
const READ_METHODS = new Set(['get', 'head', 'options']);

/** Lets a caller override the read/write default for a single request. */
export interface RefusalNotifiableConfig extends InternalAxiosRequestConfig {
    notifyOnRefusal?: boolean;
}

// A single page render can fire several requests that are all refused at once
// (see #14600: one per evaluation type). Collapse that burst into one toast
// while still letting a genuinely new refusal, later on, notify the user again.
const REFUSAL_TOAST_COOLDOWN_MS = 3000;

let lastRefusalToastAt = 0;

// Tracked separately from the toast: a refused read no longer interrupts the
// user (#14883), but consumers that only see a boolean `isError` still need to
// know the failure was a refusal rather than a fault, so they do not replace a
// silenced specific message with a vague generic one.
let lastRefusedAt = 0;

const notifyRefused = (status?: number) => {
    const now = Date.now();

    if (now - lastRefusalToastAt < REFUSAL_TOAST_COOLDOWN_MS) {
        return;
    }

    lastRefusalToastAt = now;

    showErrorToast(
        status === 404
            ? {
                  title: i18n.t('errors.notFoundOrInaccessibleTitle'),
                  message: i18n.t('errors.notFoundOrInaccessibleMessage'),
              }
            : {
                  title: i18n.t('errors.insufficientPermissionsTitle'),
                  message: i18n.t('errors.insufficientPermissionsMessage'),
              },
    );
};

/**
 * True when a refusal (a 403, or a 404 on a write) was reported in the last
 * cooldown window.
 *
 * Consumers that only observe a boolean `isError` (see `useFetchErrorsHandler`)
 * cannot tell a refusal from a 500, and would otherwise add a generic "an error
 * occurred" toast on top of the specific message shown here.
 */
export const hasRecentlyReportedRefusal = () =>
    Date.now() - lastRefusalToastAt < REFUSAL_TOAST_COOLDOWN_MS;

/**
 * True when a refusal occurred recently, whether or not it was announced: a 403
 * on any request, or a 404 on a write. A read answered 404 is left out, because
 * its caller often expects it. `useFetchErrorsHandler` consults this rather than
 * `hasRecentlyReportedRefusal`: a silently refused read must still suppress the
 * generic "an error occurred" message, which would be less accurate than the
 * specific one it replaced.
 */
export const hasRecentlyBeenRefused = () => Date.now() - lastRefusedAt < REFUSAL_TOAST_COOLDOWN_MS;

/** Clears the cooldown. Exists so tests can start from a known state. */
export const resetRefusalNotifications = () => {
    lastRefusalToastAt = 0;
    lastRefusedAt = 0;
};

/**
 * Whether a refused request is worth interrupting the user for.
 *
 * A write is something they did: saving, assigning, deleting. Being told why it
 * was refused is the whole point. A read is usually a page or a component
 * fetching what it needs, and refusing one says nothing the user asked to hear
 * — they see a page that works, and a toast about a permission they never
 * exercised (#14883). So reads stay quiet unless they opt in.
 *
 * A read the user genuinely triggered, say a filter that refetches, can set
 * `notifyOnRefusal: true` on its request config to be treated as a write.
 */
const shouldNotifyRefusal = (config?: InternalAxiosRequestConfig) => {
    const explicit = (config as RefusalNotifiableConfig | undefined)?.notifyOnRefusal;

    if (explicit !== undefined) {
        return explicit;
    }

    return !isReadRequest(config);
};

const isReadRequest = (config?: InternalAxiosRequestConfig) =>
    READ_METHODS.has((config?.method ?? 'get').toLowerCase());

api.interceptors.response.use(
    (response) => {
        addApiRequestBreadcrumb(response);
        return response;
    },
    (error) => {
        addApiErrorBreadcrumb(error);

        // A refusal is expected behaviour rather than a fault. Tell the user why
        // their change was refused, then reject as usual so React Query still
        // surfaces the error state.
        if (isRefusedError(error)) {
            // A read answered 404 is often an absence its caller expects and turns
            // into "nothing yet": recording it would silence a real failure that
            // follows within the cooldown.
            if (statusOf(error) === 403 || !isReadRequest(error?.config)) {
                lastRefusedAt = Date.now();
            }

            if (shouldNotifyRefusal(error?.config)) {
                notifyRefused(statusOf(error));
            }
        }

        return Promise.reject(error);
    },
);

api.interceptors.request.use((config) => {
    // Demo protection - block write operations for demo users
    // Only apply to requests going to our API (relative paths or our API URL)
    // External third-party APIs (Stripe, Intercom, etc.) are not blocked
    const isPerformanceApiRequest = config.url?.startsWith('/') || config.url?.includes(API_URL);

    if (
        isDemoUser() &&
        isPerformanceApiRequest &&
        isWriteOperation(config.method || 'GET', config.url)
    ) {
        showErrorToast({ message: i18n.t('common.demoRestriction') });
        // Create a cancelled error that can be identified by error handlers
        const cancelledError = new axios.Cancel('Demo restriction - request cancelled');
        // Mark this error as a demo restriction so error handlers can ignore it
        Object.defineProperty(cancelledError, 'isDemoRestriction', { value: true });
        return Promise.reject(cancelledError);
    }

    // Dev token takes priority for local development
    const devToken = import.meta.env.VITE_DEV_TOKEN;

    // Production: read from localStorage (set by host app); guard for non-browser/test runtimes.
    const storedToken =
        typeof localStorage === 'undefined' ? null : localStorage.getItem(TOKEN_STORAGE_KEY);
    const token = devToken || storedToken;

    if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
    }

    config.headers.set('Accept-Language', i18n.language);

    return config;
});

export { api, isTest, mockUrl, TOKEN_STORAGE_KEY };
